import React, { useEffect, useState, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faInfoCircle,
  faVideo,
  faPhone,
  faMars,
  faVenus,
  faTransgender,
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import useAuth from '../../../hooks/useAuth'
import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import DependantForm from '../../../components/DependantForm'
import Onboarding from '../Onboarding'
import OnboardingComplete from './OnboardingComplete'
import Loader from '../../../components/Loader'
const StepTwo = ({
  setSelectedAppointment,
  selectedAppointment,
  setStep,
  onValidityChange,
  setDependant,
  dependant
}) => {
  const { auth } = useAuth()
  const [activeDependant, setActiveDependant] = useState(null);
  const [consultationType, setConsultationType] = useState('Video')
  const [preferredGender, setPreferredGender] = useState('Either')
  const [appointmentDate, setAppointmentDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [appointments, setAppointments] = useState({})
  const [allergies, setAllergies] = useState([])
  const [medications, setMedications] = useState([])
  const [conditions, setConditions] = useState([])
  const [openSection, setOpenSection] = useState('')
  const [appointmentsByDate, setAppointmentsByDate] = useState({})
  const [loadingAppointments, setLoadingAppointments] = useState(false)
  const [consultationFor, setConsultationFor] = useState('myself')
  const [showDependantForm, setShowDependantForm] = useState(false)
  const [dependants, setDependants] = useState(null);
  const [localDependants, setLocalDependants] = useState([]);
  const [selectedDependant, setSelectedDependant] = useState(null);
  const [isOpen, setIsOpen] = useState(dependants?.length > 0);
  const [showCompletedOnboardingModal, setShowCompleteOnboardingModal] = useState(false)
  const [showOnboardingModal, setShowOnboardingModal] = useState(false)
  const [clientCreationErrors, setClientCreationErrors] = useState({})
  const [creatingClient, setCreatingClient] = useState(false)
  const [hasClient, setHasClient] = useState(auth?.user?.health_hero_id ? true : false)
  const [failedDependants, setFailedDependants] = useState([])
  const axios = useAxiosPrivate()

  const selectedAppointments = appointmentsByDate[appointmentDate] || []

  const morningSlots = selectedAppointments.filter((a) => {
    // Parse UTC time, convert to local time
    const hour = moment(a.attributes.start).hour()
    return hour >= 8 && hour < 12
  })

  const afternoonSlots = selectedAppointments.filter((a) => {
    const hour = moment(a.attributes.start).hour()
    return hour >= 12 && hour < 17
  })

  const eveningSlots = selectedAppointments.filter((a) => {
    const hour = moment(a.attributes.start).hour()
    return hour >= 17 && hour < 22
  })

  useEffect(() => {
    fetchDependants();
  }, [])

  useEffect(() => {
    if (!hasClient) {
      onValidityChange(false)
      setCreatingClient(true)
      createClientPatientRecord()
    }
    fetchAppointments()
  }, [consultationType, preferredGender])

  useEffect(() => {
    const valid = validate()
    if (typeof onValidityChange === 'function') {
      onValidityChange(valid)
    }
  }, [selectedTime, preferredGender])

  useEffect(() => {
    if (dependant) {
      setActiveDependant(dependant);
    }
  }, [dependant])

  const fetchDependants = async () => {
    try {
      const response = await axios.get(
        `/users/${auth.user.id}/dependants`
      )
      setDependants(response.data.result?.data ?? []);
      setFailedDependants(response.data.result.failed_dependants ?? [])
      setLocalDependants(auth.user.dependants || []);
    } catch (error) {
      console.error('Error fetching resources:', error)
    }
  }

  // Map API dependants to local dependants with matching name + DOB
  const mappedDependants = useMemo(() => {
    if (!dependants || !localDependants) return [];

    return dependants.map((apiDep) => {
      const match = localDependants.find((localDep) =>
        (apiDep.id === localDep.health_hero_id) || (localDep.name.toLowerCase() === apiDep.attributes.fullName.toLowerCase() &&
        localDep.date_of_birth === apiDep.attributes.dateOfBirth)
      );

      return {
        ...apiDep,
        localId: match ? match.id : null, // attach local dependant ID
      };
    });
  }, [dependants, localDependants]);



  const selectDependantClick = (dependant) => {
    setSelectedDependant(dependant);
    setDependant(dependant); // Pass to parent component
    setActiveDependant(dependant); // Track selected dependant
    setShowDependantForm(false);
  }


  const validate = () => {
    return (
      selectedTime !== '' &&
      preferredGender !== ''
    )
  }

  const fetchAppointments = async () => {
    setLoadingAppointments(true)
    try {
      const response = await axios.get('/appointments/all', {
        params: {
          consultationType: consultationType,
          preferredGender: preferredGender,
        },
      });

      const rawAppointments = response?.data?.result.data;

      if (Array.isArray(rawAppointments)) {
        setAppointments(rawAppointments)
        const grouped = groupAppointments(rawAppointments)
        setAppointmentsByDate(grouped)

        // Flatten all appointments into a single array
        const allAppointments = Object.values(grouped).flat();

        // Filter only appointments that start at or after 08:00 AM
        const filteredAppointments = allAppointments.filter((appt) => {
          const start = new Date(appt.attributes.start);
          return start.getHours() >= 9; // only 08:00 AM or later
        });

        if (filteredAppointments.length > 0) {
          // Find the one with the earliest start time among filtered ones
          const earliestAppointment = filteredAppointments.reduce((earliest, current) => {
            const currentStart = new Date(current.attributes.start);
            const earliestStart = new Date(earliest.attributes.start);
            return currentStart < earliestStart ? current : earliest;
          });

          // Set the date group that matches the earliest appointment
          const earliestDate = moment(earliestAppointment.attributes.start).format('YYYY-MM-DD');
          setAppointmentDate(earliestDate);
          handleSlotClick(earliestAppointment); // sets selectedTime properly
        }
      } else {
        console.warn('Invalid appointments response:', rawAppointments)
        setAppointments([])
        setAppointmentsByDate({})
      }
    } catch (error) {
      console.error('Error fetching appointments:', error)
      setAppointments([])
      setAppointmentsByDate({})
    } finally {
      setLoadingAppointments(false)
    }
  }


  const createClientPatientRecord = async () => {
    await axios
      .post('/clients/healthhero/create', { user_id: auth?.user?.id })
      .then((response) => {
        if (response.data.error) {
          //Passed our requirements but failed on health hero's
          const result = response.data.result
          if (result[0]?.code === "400_validation") {
            let validationErrors = []
            for (let i = 1; i<result?.length; i++) {
              const splitCode = result[i]?.code?.split(".")
              const codeType = splitCode[0] + '.' + splitCode[1]
              let validationMessage = ''
              switch (codeType) {
                case 'PatientRecord.Gp':
                  validationMessage  = 'GP details incomplete'
                  break;
                case 'PatientRecord.PhoneNumber':
                  validationMessage  = 'Phone number invalid'
                  break;
                case 'PatientRecord.DateOfBirth':
                  validationMessage  = 'Date of Birth is invalid'
                  break;
                case 'PatientRecord.Address':
                  validationMessage  = 'Address is  incomplete'
                  break;
                default:
                  validationMessage  = 'Please ensure profile information is complete'
                  break;
              }

              if (!validationErrors[validationMessage]) {
                validationErrors[validationMessage] = []
              }
              if (codeType==='PatientRecord.Gp' && splitCode.length >= 5 && splitCode[3] === 'Address') {
                validationErrors[validationMessage].push(splitCode[4].split(/(?=[A-Z0-9])/).join(" "))
              }
              if (codeType==='PatientRecord.Address' && splitCode.length >= 3) {
                validationErrors[validationMessage].push(splitCode[2].split(/(?=[A-Z0-9])/).join(" "))
              }
            }
            onValidityChange(false)
            setHasClient(false)
            setClientCreationErrors(validationErrors)
          }  else if (result[0]?.code === 409) {
            //client already exists, shouldn't hit
            onValidityChange(true)
            setHasClient(true)
            setClientCreationErrors({})
          } else {
            onValidityChange(false)
            setHasClient(false)
            setClientCreationErrors({'Please ensure profile information is complete': []})
          }

          setCreatingClient(false)
        } else {
          //created fine
          setCreatingClient(false)
          setHasClient(true)
          onValidityChange(true)
          setClientCreationErrors({})
        }
        // handle success
        // toast.success('Client record created successfully');
      })
      .catch((error) => {
        // handle error
        setCreatingClient(false)
        setHasClient(false)
        onValidityChange(false)
        setClientCreationErrors(['Please ensure profile information is complete'])
      })
  }

  const groupAppointments = (appointments) => {
    if (!Array.isArray(appointments)) return {}
    const grouped = {}
    const seenStartTimes = new Set()

    appointments.forEach((item) => {
      const start = item?.attributes?.start
      if (!start) return

      // Skip if we've already seen this time
      if (seenStartTimes.has(start)) return
      seenStartTimes.add(start)

      const startDate = moment(start).format('YYYY-MM-DD')
      if (!grouped[startDate]) grouped[startDate] = []
      grouped[startDate].push(item)
    })

    return grouped
  }

  const getPractitionerLabel = () => {
    if (preferredGender === 'male') return 'Male General Practitioner'
    if (preferredGender === 'female') return 'Female General Practitioner'
    return 'General Practitioner'
  }

  const options = useMemo(() => {
    if (!appointmentsByDate || Object.keys(appointmentsByDate).length === 0)
      return []

    // Extract all dates, sorted ascending
    const dates = Object.keys(appointmentsByDate).sort()

    return dates.map((dateStr, idx) => {
      const dateMoment = moment(dateStr, 'YYYY-MM-DD')
      let label

      if (dateMoment.isSame(moment(), 'day')) {
        label = 'Today'
      } else if (dateMoment.isSame(moment().add(1, 'day'), 'day')) {
        label = 'Tomorrow'
      } else {
        label = dateMoment.format('ddd D MMM')
      }

      return { label, value: dateStr }
    })
  }, [appointmentsByDate])


  const consultationForClicked = (value) => {
    if (value === 'myself') {
      setDependant(null);
      setActiveDependant(null);
    }
    setConsultationFor(value)
  }


  const handleConsultationTypeChange = (value) => {
    setSelectedAppointment(prev => ({
      ...(prev || {}),
      attributes: {
        ...(prev?.attributes || {}),
        consultationType: value,
      },
    }));
    setConsultationType(value);
  };


  const handleSlotClick = ({ attributes }) => {
    const appointmentId = attributes?.appointmentIds?.[0];
    setSelectedTime(attributes.start);  // store start time string here
    setAppointmentDate(moment(attributes.start).format('YYYY-MM-DD'));
    if (appointmentId) {
      fetchSelectedAppointment(appointmentId)
    } else {
      console.warn('No appointment ID found in slot')
    }
  }

  const fetchSelectedAppointment = async (id) => {
    try {
      const { data } = await axios.get(`/appointments/selected/${id}`);
      const fetched = data?.result?.data || {};

      setSelectedAppointment(prev => ({
        ...(prev || {}),
        ...fetched,
        practitionerGender: preferredGender,
        attributes: {
          ...(prev?.attributes || {}),
          ...(fetched?.attributes || {}),
          // Keep existing consultationType if already set
          consultationType: prev?.attributes?.consultationType || fetched?.attributes?.consultationType || consultationType,
        },
      }));
    } catch (error) {
      console.error('Error fetching selected appointment:', error);
    }
  };

  const handleSetPrefferedGender = (value) => {
    setPreferredGender(value)
    setSelectedAppointment({
      ...selectedAppointment,
      practitionerGender: preferredGender,
    })
  }

  return (
    <div>
      {!creatingClient && Object.keys(clientCreationErrors)?.length < 1 ? (
        <div className="max-w-3xl mx-auto p-6 space-y-8 text-portal-dark-blue">
          {/* Info Panel */}
          <div className="bg-gray-100 border rounded-lg p-4 text-sm ">
            <p className="font-semibold mb-2">About your consultation</p>
            <p className="mb-2">
              The service is not a replacement for any regular health service(s) you
              are normally registered with. You may still need to see a healthcare
              professional face to face or be advised to contact the emergency
              services if the clinician feels it is necessary.
            </p>
            <ul className="list-disc list-inside mb-2">
              <li>
                Repeat Prescriptions except some forms of contraceptive pills and
                some forms of HRT
              </li>
              <li>Prescriptions for controlled drugs</li>
              <li>Treatment for Ongoing Conditions</li>
            </ul>
            <p>
              Please review{' '}
              <Link to="/terms" target='_blank' className="text-blue-600 underline">
                our service terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy" target='_blank' className="text-blue-600 underline">
                Privacy Policy
              </Link>{' '}
              to understand what we can provide and how we do so.
            </p>
          </div>

          {/* Consultation For */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Who is the consultation for? <FontAwesomeIcon icon={faInfoCircle} /></h2>
            <div className="grid grid-cols-2 gap-4">
              <button type="button" onClick={() => consultationForClicked('myself')} className={`border rounded-lg py-3 font-medium ${consultationFor === 'myself' ? 'bg-light-purple border-light-purple' : 'bg-white'}`}>Myself</button>
              <button type="button" onClick={() => consultationForClicked('dependant')} className={`border rounded-lg py-3 font-medium ${consultationFor === 'dependant' ? 'bg-light-purple border-light-purple' : 'bg-white'}`}>My dependant</button>
            </div>

            {consultationFor === 'dependant' && (
              <>
                {Object.keys(failedDependants)?.length > 0 && (
                  <div className='bg-amber-100/60 border border-amber-300 rounded-lg p-2 mt-2'>
                    <p className='ml-2 font-bold'>The dependants below could not be loaded for the following reasons:</p>
                    {Object.keys(failedDependants).map((failedDepKey) => {
                      return (
                        failedDependants[failedDepKey].map((failInfoItem, key) => {
                          if (key === 0) {
                            return (
                              <div className='ml-5 font-semibold mt-2' key={'failed_dependant_'+failedDepKey}>
                                {failedDependants[failedDepKey][key]} :
                              </div>
                            )
                          } else {
                            return (
                              <div className='ml-10' key={'failed_dependant_'+failedDepKey}>
                                <span className='text-lg mr-2'>&bull;</span>{failedDependants[failedDepKey][key]}
                              </div>
                            )
                          }
                        })
                      )
                    })}
                  </div>
                )}
                <div className="flex flex-col items-end gap-4 w-full mt-4">
                  {/** State to toggle showing DependantForm */}
                  {showOnboardingModal ? (
                    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-[3px] z-50 flex items-center justify-center">
                      <div className="bg-white rounded-lg p-6 shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
                        {/* <h2 className="text-2xl font-bold mb-4">Complete Your Profile</h2> */}
                        <Onboarding
                          setShowOnboardingCompletedModal={setShowCompleteOnboardingModal}
                          setShowOnboardingModal={setShowOnboardingModal}
                          dependant={true}
                          onComplete={() => {
                            fetchDependants()
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <>

                      {/* Dependants Selector */}
                      <div className="w-full">
                        <div className="w-full flex items-center gap-4">
                          <div className="w-full">
                            <h3 className="flex m-0">
                              <button
                                type="button"
                                disabled={!dependants?.length}
                                onClick={() =>
                                  dependants?.length && setIsOpen((prev) => !prev)
                                }
                                className={`group flex-1 flex items-center justify-between px-4 py-3 text-base font-bold leading-6 transition ease-in-out
                                  ${dependants?.length
                                    ? 'bg-gray-100 cursor-pointer'
                                    : 'bg-gray-100 cursor-not-allowed text-gray-400'
                                  }
                                  border-l border-r border-t focus:outline-none
                                  ${isOpen
                                    ? 'rounded-t-lg rounded-b-none border-b-0'
                                    : 'shadow rounded-lg border-b'
                                  }
                                `}
                              >
                                <div className="flex items-center gap-2">
                                  <span>Dependants</span>
                                  <span className="font-light text-sm">
                                    ({dependants?.length || 0} available)
                                  </span>
                                </div>
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  className={`transition-transform duration-200 ${isOpen ? 'rotate-180 text-gray-600' : 'text-gray-600'
                                    }`}
                                >
                                  <path
                                    d="M19.5 9L12 16.5L4.5 9"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </h3>

                            {/* Dropdown */}
                            {mappedDependants?.length > 0 && isOpen && (
                              <div
                                className={`bg-gray-100 border-l border-r border-b -mt-[1px] p-3 flex flex-wrap gap-2
                                  ${isOpen
                                    ? 'rounded-b-lg rounded-t-none border-t-0'
                                    : 'rounded-lg border-t'
                                  }
                                `}
                              >
                                {mappedDependants?.map((dep, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    disabled={!dep.localId}
                                    onClick={() => {
                                      if (dep.type_id !== 5) {
                                        selectDependantClick(dep); // dep.localId is now accessible inside this handler
                                      }
                                    }}
                                    className={`px-4 py-2 text-md rounded-md shadow transition
                                    ${!dep.localId
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : activeDependant?.attributes.fullName === dep.attributes.fullName
                                          ? 'bg-light-purple border-light-purple'
                                          : 'bg-white text-gray-800 hover:bg-light-purple'
                                      }
          `}
                                  >
                                    {dep.attributes.fullName}
                                  </button>
                                ))}

                              </div>
                            )}
                          </div>
                        </div>
                      </div>




                      {/* Add Dependant Button */}
                      <button
                        type="button"
                        onClick={() => setShowOnboardingModal(true)}
                        className="bg-[#E7CDDB] border-2 border-[#E7CDDB] text-[#41264B] font-medium px-5 py-1.5 rounded-md hover:bg-white transition"
                      >
                        <span>Add Dependant</span>
                      </button>
                    </>
                  )}
                </div>
              </>
            )}





          </div>

          {/* Consultation Type */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Consultation type</h2>
            <div className="grid grid-cols-2 gap-4">
              <button type="button" onClick={() => handleConsultationTypeChange('Video')} className={`border rounded-lg py-3 font-medium flex items-center justify-center gap-2 ${consultationType === 'Video' ? 'bg-light-purple border-light-purple' : 'bg-white'}`}>
                <FontAwesomeIcon icon={faVideo} /> Video call
              </button>
              <button type="button" onClick={() => handleConsultationTypeChange('Phone')} className={`border rounded-lg py-3 font-medium flex items-center justify-center gap-2 ${consultationType === 'Phone' ? 'bg-light-purple border-light-purple' : 'bg-white'}`}>
                <FontAwesomeIcon icon={faPhone} /> Phone call
              </button>
            </div>
          </div>

          {/* Preferred Gender */}
          <div>
            <h2 className="text-lg font-semibold mb-2">
              Preferred gender of HealthHero clinician
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => handleSetPrefferedGender('Either')}
                className={`border rounded-lg py-3 font-medium flex items-center justify-center gap-2 ${preferredGender === 'Either'
                  ? 'bg-light-purple border-light-purple'
                  : 'bg-white'
                  }`}
              >
                <FontAwesomeIcon icon={faTransgender} /> Either
              </button>
              <button
                type="button"
                onClick={() => handleSetPrefferedGender('Male')}
                className={`border rounded-lg py-3 font-medium flex items-center justify-center gap-2 ${preferredGender === 'Male'
                  ? 'bg-light-purple border-light-purple'
                  : 'bg-white'
                  }`}
              >
                <FontAwesomeIcon icon={faMars} /> Male
              </button>
              <button
                type="button"
                onClick={() => handleSetPrefferedGender('Female')}
                className={`border rounded-lg py-3 font-medium flex items-center justify-center gap-2 ${preferredGender === 'Female'
                  ? 'bg-light-purple border-light-purple'
                  : 'bg-white'
                  }`}
              >
                <FontAwesomeIcon icon={faVenus} /> Female
              </button>
            </div>
          </div>

          {Object.keys(appointments).length ? (
            <>
              <h2 className='text-lg font-semibold mb-2'>Appointment date and time</h2>
              <p>Available Monday - Sunday 08:00 - 22:00 (UK time).</p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {options.map(({ label, value }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAppointmentDate(value)}
                    className={`border rounded-lg py-2 font-medium transition 
                        ${appointmentDate === value
                        ? 'bg-light-purple border-light-purple'
                        : 'bg-white'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {appointmentDate && (
                <div className="mt-6 space-y-4">
                  {/* Morning Accordion */}
                  <div className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        setOpenSection(openSection === 'morning' ? '' : 'morning')
                      }}
                      className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100"
                    >
                      <span className="font-medium">
                        Morning {loadingAppointments ? (<FontAwesomeIcon icon={faSpinner} className='ml-2 animate-spin'/>) : `(${morningSlots.length} available)`}
                      </span>
                      <FontAwesomeIcon
                        icon={
                          openSection === 'morning' ? faChevronUp : faChevronDown
                        }
                        className="text-gray-500"
                      />
                    </button>
                    {openSection === 'morning' && (
                      <div className="p-4 bg-white">
                        <div className="grid grid-cols-3 gap-2">
                          {!loadingAppointments && morningSlots.map((slot) => (

                            //add check ehre to see if the slot time is before the current time
                            <button
                              type="button"
                              key={slot.attributes.appointmentIds[0]}
                              onClick={(e) => {
                                e.preventDefault()
                                handleSlotClick(slot)
                              }}
                              className={`rounded-md py-2 px-3 text-sm font-medium border 
          ${selectedTime === slot.attributes.start
                                  ? 'bg-light-purple border-light-purple text-white'
                                  : 'bg-white hover:bg-gray-50'}`}
                            >
                              {moment(slot.attributes.start).format('HH:mm')}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Afternoon Accordion */}
                  <div className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        setOpenSection(
                          openSection === 'afternoon' ? '' : 'afternoon'
                        )
                      }}
                      className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100"
                    >
                      <span className="font-medium">
                        Afternoon {loadingAppointments ? (<FontAwesomeIcon icon={faSpinner} className='ml-2 animate-spin'/>) : `(${afternoonSlots.length} available)`}
                      </span>
                      <FontAwesomeIcon
                        icon={
                          openSection === 'afternoon' ? faChevronUp : faChevronDown
                        }
                        className="text-gray-500"
                      />
                    </button>
                    {openSection === 'afternoon' && (
                      <div className="p-4 bg-white">
                        <div className="grid grid-cols-3 gap-2">
                          {!loadingAppointments && afternoonSlots.map((slot) => (
                            <button
                              type="button"
                              key={slot.attributes.start}
                              onClick={(e) => {
                                e.preventDefault()
                                handleSlotClick(slot)
                              }}
                              className={`rounded-md py-2 px-3 text-sm font-medium border 
          ${selectedTime === slot.attributes.start
                                  ? 'bg-light-purple border-light-purple text-white'
                                  : 'bg-white hover:bg-gray-50'}`}
                            >
                              {moment(slot.attributes.start).format('HH:mm')}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Evening Accordion */}
                  <div className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        setOpenSection(openSection === 'evening' ? '' : 'evening')
                      }}
                      className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100"
                    >
                      <span className="font-medium">
                        Evening {loadingAppointments ? (<FontAwesomeIcon icon={faSpinner} className='ml-2 animate-spin'/>) : `(${eveningSlots.length} available)`}
                      </span>
                      <FontAwesomeIcon
                        icon={
                          openSection === 'evening' ? faChevronUp : faChevronDown
                        }
                        className="text-gray-500"
                      />
                    </button>
                    {openSection === 'evening' && (
                      <div className="p-4 bg-white">
                        <div className="grid grid-cols-3 gap-2">
                          {!loadingAppointments && eveningSlots.map((slot) => (
                            <button
                              type="button"
                              key={slot.attributes.start}
                              onClick={(e) => {
                                e.preventDefault()
                                handleSlotClick(slot)
                              }}
                              className={`rounded-md py-2 px-3 text-sm font-medium border 
                                ${selectedTime === slot.attributes.start
                                  ? 'bg-light-purple border-light-purple text-white'
                                  : 'bg-white hover:bg-gray-50'
                                }`}
                            >
                              {moment(slot.attributes.start).format('HH:mm')}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedTime && (
                    <div className="border rounded-lg font-medium flex p-3 gap-2 bg-white">
                      <p className="text-sm text-gray-600">
                        You have selected:
                        <span className='font-bold capitalize'> {consultationType} call</span> with a<span className='font-bold ml-2'>{getPractitionerLabel()}</span> on <span className='font-bold'>{selectedTime ? moment(selectedTime).format('dddd, MMMM D, YYYY [at] HH:mm') : 'no appointment'}</span>.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : loadingAppointments ? (
            <p>Loading Appointments <FontAwesomeIcon icon={faSpinner} className='ml-2 animate-spin'/></p>
          ) : (
            <p>No available appointments</p>
          )}
        </div>
      ) : creatingClient ? (
        <div>
          <Loader />
        </div>
      ) : (
        <div className='flex justify-center'>
          <div className='flex space-x-5 rounded-lg bg-amber-200 w-3/5 border-amber-600 border p-2 pl-4 text-amber-800 pb-4'>
          <FontAwesomeIcon icon={faCircleInfo} className="mt-3 text-xl" />
          <div>
            <p className='text-lg font-semibold pt-2 mb-2'>Consultation cannot be booked until the following profile issues are resolved:</p>
            {Object.keys(clientCreationErrors)?.map(key => {
              return (
                <p className='pl-2 text-md py-1'>
                  <span classname>&bull;</span> {key} {clientCreationErrors[key]?.length>0 ? ': ' + clientCreationErrors[key].map((subError) => {return ' '+subError}) : ''}
                </p>
              )
            })}
            </div>
          </div>
        </div>
      )}
    </div>

  )
}

export default StepTwo
